import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { TrebleClefIcon } from "@/components/TrebleClefLogo";

const Register = () => {
  const navigate = useNavigate();
  const { register, user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Welcome to Brown𝄞tone Jazz Club!");
      navigate("/membership");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-4 pt-20 pb-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <TrebleClefIcon size={48} />
            <span className="font-display text-2xl">
              <span className="text-platinum">Brown</span>
              <span className="text-electric inline-block mx-0.5" style={{ transform: 'scaleX(-1.3)' }}>𝄞</span>
              <span className="text-platinum">tone</span>
            </span>
          </Link>
          <p className="text-white/50 mt-2">Join our jazz community</p>
        </div>

        {/* Register Form */}
        <div className="bg-obsidian-light border border-white/10 rounded-lg p-8" data-testid="register-form">
          <h1 className="font-display text-2xl text-white mb-6 text-center">Create Account</h1>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white/70">Full Name</Label>
              <Input 
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                className="bg-obsidian border-white/20 text-white placeholder:text-white/40 focus:border-electric"
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70">Email</Label>
              <Input 
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-obsidian border-white/20 text-white placeholder:text-white/40 focus:border-electric"
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/70">Password</Label>
              <div className="relative">
                <Input 
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-obsidian border-white/20 text-white placeholder:text-white/40 focus:border-electric pr-10"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white/70">Confirm Password</Label>
              <Input 
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-obsidian border-white/20 text-white placeholder:text-white/40 focus:border-electric"
                data-testid="register-confirm-password"
              />
            </div>

            <Button 
              type="submit"
              disabled={loading}
              className="w-full bg-electric text-obsidian hover:bg-electric-light py-6 font-medium"
              data-testid="register-submit"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-obsidian border-t-transparent rounded-full animate-spin"></span>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-white/50 text-sm">
              Already have an account?{" "}
              <Link to="/login" className="text-electric hover:underline">
                Sign In
              </Link>
            </p>
          </div>
        </div>

        <p className="text-white/30 text-xs text-center mt-6">
          By creating an account, you agree to receive updates 
          about upcoming events at Brown𝄞tone Jazz Club.
        </p>
      </div>
    </div>
  );
};

export default Register;
